import { useMonaco } from '@monaco-editor/react';
import { useEffect } from 'react';

import {
  createFormulaDependencyProposals,
  createMiscConstantFormulaDependencyProposals,
} from '@finley-technologies/calc-agent';
import {
  FormulaEngine,
  CreditAgreement,
} from '@finley-technologies/finley-types';

import { MonacoType } from '../../../../types/global.d';

/**
 * NOTE: once there's more focus around better / more universal Monaco Editor management,
 * shift this utility function and lanaguage to a more global placement
 */
const createMonacoCompletionItems = (
  proposals: FormulaEngine.FormulaDependencyType[],
  completionInputs: Pick<
    MonacoType.languages.CompletionItem,
    'range' | 'kind' | 'insertTextRules'
  >,
): MonacoType.languages.CompletionItem[] => {
  const { kind, range, insertTextRules } = completionInputs;
  return proposals.map((field) => ({ ...field, range, kind, insertTextRules }));
};

export const REPORTING_XLSX_EDITOR_LANGUAGE = 'reporting_xlsx_output_data_cell';

export const useReportingMonacoEditor = (params: {
  queryResults: CreditAgreement.Config.QueryResult[];
}): void => {
  const { queryResults } = params;

  const monaco = useMonaco();

  useEffect(() => {
    if (!monaco) {
      return () => null;
    }

    const formulaPrettyPrintProposals: FormulaEngine.FormulaDependencyType[] = createFormulaDependencyProposals(
      { prettyPrint: true },
    );
    const miscFormulaPropsals: FormulaEngine.FormulaDependencyType[] = createMiscConstantFormulaDependencyProposals();
    const queryResultProposals: FormulaEngine.FormulaDependencyType[] = queryResults.map(
      ({ name, value }) => ({
        label: name,
        insertText: name,
        documentation: typeof value,
      }),
    );

    monaco.languages.register({ id: REPORTING_XLSX_EDITOR_LANGUAGE });
    const provider = monaco.languages.registerCompletionItemProvider(
      REPORTING_XLSX_EDITOR_LANGUAGE,
      {
        provideCompletionItems: (model, position) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          const formulaSuggestions = createMonacoCompletionItems(
            formulaPrettyPrintProposals,
            {
              range,
              kind: monaco.languages.CompletionItemKind.Function,
              insertTextRules:
                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            },
          );

          const miscFormulaSuggestions = createMonacoCompletionItems(
            miscFormulaPropsals,
            { range, kind: monaco.languages.CompletionItemKind.Constant },
          );

          const fieldSuggestions = createMonacoCompletionItems(
            queryResultProposals,
            { range, kind: monaco.languages.CompletionItemKind.Variable },
          );

          return {
            suggestions: [
              ...formulaSuggestions,
              ...miscFormulaSuggestions,
              ...fieldSuggestions,
            ],
          };
        },
      },
    );

    return () => {
      provider.dispose();
    };
  }, [monaco, queryResults]);
};
